import { isNil, path, pathOr, propOr, isEmpty } from '../../../utils/fp';
import * as rules2 from '../../rules2';
import { urn2FullRef } from '@dbmx/sdmx/core.js';
import { getAttributes } from '@dbmx/sdmx/datastructures';
import { renameKeys } from '../../../utils';

/*
 * structure accessors should always and only take a valid SDMX structure as param
 * basic format of an accesor is: structure => {}
 * if a context is needed, use a fof: ctx => structure => {}
 */

const DFs = 'dataflows';
const DSDs = 'dataStructures';
const CODELISTs = 'codelists';
const CONCEPT_SCHEMEs = 'conceptSchemes';
const MSDs = 'metadataStructures';

const getHeadDataByKey = key => structure => pathOr({}, ['data', key, 0], structure);
const getDatasByKey = key => structure => pathOr([], ['data', key], structure);
export const getDataStructure = getHeadDataByKey(DSDs);
export const getMetadataStructure = getHeadDataByKey(MSDs);
export const getCodeLists = getDatasByKey(CODELISTs);
export const getConceptSchemes = getDatasByKey(CONCEPT_SCHEMEs);
const getDataflow = getHeadDataByKey(DFs);
export const getDataflowName = structure => getDataflow(structure).name;
export const getDataflowDescription = structure => getDataflow(structure).description;
const getAnnotationsByKey = key => structure =>
  propOr([], 'annotations', getHeadDataByKey(key)(structure));

export const getDataStructureAnnotations = getAnnotationsByKey(DSDs);
export const getDataflowAnnotations = getAnnotationsByKey(DFs);

export const getCustomDataRequestSizeAnnotation = structure =>
  getDataflowAnnotations(structure).find(annotation => annotation.type === 'MAX_TABLE_DATA');

const getHiddenValuesAnnotation = accessor => structure =>
  accessor(structure).find(annotation => annotation.type === 'NOT_DISPLAYED');

export const getObservationsType = structure =>
  path(
    [
      'dataStructureComponents',
      'measureList',
      'primaryMeasure',
      'localRepresentation',
      'textFormat',
      'textType',
    ],
    getDataStructure(structure),
  );

const haslayoutCellAlign = accessor => structure =>
  accessor(structure).find(annotation => annotation.type === 'LAYOUT_CELL_ALIGN');

export const getTextAlignAnnotation = locale => structure => {
  const annotation =
    haslayoutCellAlign(getDataflowAnnotations)(structure) ||
    haslayoutCellAlign(getDataStructureAnnotations)(structure);
  return path(['texts', locale], annotation) || annotation?.title;
};

export const getAllHiddenValuesAnnotation = structure => {
  const annotation =
    getHiddenValuesAnnotation(getDataflowAnnotations)(structure) ||
    getHiddenValuesAnnotation(getDataStructureAnnotations)(structure);
  return isNil(annotation) ? {} : rules2.getNotDisplayedCombinations(annotation);
};

const hasTimePeriodDesc = accessor => structure =>
  !isNil(accessor(structure).find(annotation => annotation.type === 'LAYOUT_TIME_PERIOD_DESC'));

export const getIsTimePeriodInversed = structure =>
  hasTimePeriodDesc(getDataflowAnnotations)(structure) ||
  hasTimePeriodDesc(getDataStructureAnnotations)(structure);

export const getDSDIdentifiers = structure => {
  const dataStructure = getDataStructure(structure);
  const picked = {
    agencyID: dataStructure.agencyID,
    id: dataStructure.id,
    version: dataStructure.version,
  };
  return renameKeys({ id: 'code', agencyID: 'agencyId' }, picked);
};

export const getMSDRef = structure => {
  const datastructure = getDataStructure(structure);
  const sdmxUrn = datastructure.metadata;
  const annotUrn = datastructure.annotations?.find(
    ({ type = '' }) => type.toLowerCase() === 'metadata',
  )?.title;
  const urn = sdmxUrn || annotUrn;
  if (!urn) return;
  // a malformed urn must not break the whole structure, only disable metadata
  try {
    return urn2FullRef(urn);
  } catch {
    return;
  }
};

export const getAnnotations = structure => [
  ...getDataflowAnnotations(structure),
  ...getDataStructureAnnotations(structure),
];

export const getRelatedDataAnnotation = structure =>
  getAnnotations(structure).find(annotation => annotation.type === 'RELATED_DATA')?.title;

export const getVersion = structure => getDataflow(structure).version;

export const getDisableAvailabilityAnnotation = structure =>
  getAnnotations(structure).find(annotation => annotation.type === 'DISABLE_AVAILABILITY_REQUESTS');

export const getMetadataStructureAttributes = msd => {
  const sdmxV3Attributes = getAttributes(msd);
  return isEmpty(sdmxV3Attributes)
    ? pathOr([], ['metadataStructureComponents', 'reportStructures', 0, 'metadataAttributes'], msd)
    : sdmxV3Attributes;
};
